/**
 * Single source of truth for everything the site says about Nick.
 * Editing copy should never require touching a component.
 */

export const SITE = {
  name: 'Nick Volgas',
  initials: 'NV',
  url: 'https://nickvolgas.com',
  role: 'Software Development Manager',
  employer: 'Amazon Web Services',
  location: 'Arkansas, USA',
  tagline: 'Engineering leader with AWS.',
  /** Rotated in the hero. Keep them short — they animate one word at a time. */
  roles: ['leader', 'technologist', 'rock climber', 'trail runner', 'vinyl collector', 'father'],
  email: 'n.volgas@gmail.com',
  resumeHref: 'https://resume.nickvolgas.com',
} as const

export type NavItem = { id: string; label: string }

export const NAV: NavItem[] = [
  { id: 'about', label: 'About' },
  { id: 'experience', label: 'Experience' },
  { id: 'skills', label: 'Skills' },
  { id: 'life', label: 'Off the clock' },
  { id: 'contact', label: 'Contact' },
]

export type SocialKind = 'github' | 'linkedin'

export type Social = {
  kind: SocialKind
  label: string
  href: string
  /** Shown on the contact cards under the label. */
  handle: string
  /** True when the URL still needs Nick to fill it in. */
  todo?: boolean
}

export const SOCIALS: Social[] = [
  // {
  //   kind: 'strava',
  //   label: 'Strava',
  //   href: `mailto:${SITE.email}`,
  //   handle: SITE.email,
  // },
  {
    kind: 'github',
    label: 'GitHub',
    href: 'https://github.com/volnix',
    handle: '@volnix',
  },
  {
    // TODO(nick): replace with your real LinkedIn vanity URL.
    kind: 'linkedin',
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/in/nickvolgas/',
    handle: '/in/nickvolgas',
    todo: true,
  },
]

/** Short, scannable paragraphs for the About section. */
export const ABOUT: string[] = [
  "I've been leading technical teams since 2016. These days that means AWS IoT — I manage the teams behind the AWS IoT Console.",
  "The part of the job I care most about is the people. Setting a technical direction that survives contact with reality, then coaching engineers through it, is more interesting to me than any single system I've shipped.",
  "I never stopped being hands-on. Weekends usually involve a side project somewhere between IoT and front-end development, which is how I keep enough context to be useful to the engineers I work with rather than just adjacent to them.",
  'Away from a keyboard I am a father of two little girls, rock climber, runner (mostly on trails), vinyl collector, and an occasional home-automation tinkerer.',
]

export type Role = {
  title: string
  org: string
  period: string
  /** ISO-ish start date, used for machine-readable markup. */
  start: string
  end?: string
  kind: 'work' | 'education'
  summary?: string
  highlights?: string[]
  current?: boolean
}

export const EXPERIENCE: Role[] = [
  {
    title: 'Software Development Manager',
    org: 'Amazon Web Services',
    period: '2022 — Present',
    start: '2022',
    kind: 'work',
    current: true,
    highlights: [
      'Lead the IoT Core Identity and IoT Console teams.',
      'Partner with service teams across AWS to deliver customer-obsessed solutions.',
      'Grow engineers through direct feedback and coaching, tracking performance so each of them can do their best work.',
    ],
  },
  {
    title: 'Director of Development',
    org: 'NIC, a division of Tyler Technologies',
    period: '2017 — 2022',
    start: '2017',
    end: '2022',
    kind: 'work',
    highlights: [
      'Set technical strategy against real business goals, budgets, and resource constraints.',
      'Led a team of 10 developers from conception through implementation.',
      'Spearheaded delivery of websites, APIs, and mobile applications.',
    ],
  },
  {
    title: 'IT Operations Director',
    org: 'NIC — Arkansas',
    period: '2016 — 2017',
    start: '2016',
    end: '2017',
    kind: 'work',
    summary: 'Managed infrastructure and security operations.',
  },
  {
    title: 'Senior Software Developer',
    org: 'NIC — Arkansas',
    period: '2014 — 2016',
    start: '2014',
    end: '2016',
    kind: 'work',
  },
  {
    title: 'Software Developer',
    org: 'NIC — Arkansas',
    period: '2011 — 2014',
    start: '2011',
    end: '2014',
    kind: 'work',
  },
  {
    title: 'B.S. Computer Science',
    org: 'Rhodes College',
    period: '2007 — 2011',
    start: '2007',
    end: '2011',
    kind: 'education',
  },
]

export type SkillGroup = { name: string; items: string[] }

export const SKILLS: SkillGroup[] = [
  {
    name: 'Leadership',
    items: [
      'Team building',
      'Technical strategy',
      'Coaching & mentoring',
      'Hiring',
      'Roadmap planning',
      'Cross-org partnership',
    ],
  },
  {
    name: 'Cloud & platform',
    items: ['AWS', 'AWS IoT Core', 'AI/ML', 'Observability'],
  },
  {
    name: 'Languages',
    items: ['TypeScript', 'JavaScript', 'Python'],
  },
  {
    name: 'Building things',
    items: ['React', 'Node.js', 'REST & API design', 'CSS / Sass', 'CI/CD'],
  },
]

export type Interest = {
  title: string
  blurb: string
  href?: string
  cta?: string
  icon: 'run' | 'vinyl' | 'home' | 'family' | 'mountain'
}

export const INTERESTS: Interest[] = [
  {
    title: 'Running',
    blurb:
      'Mostly trails, mostly early. I track every mile — the log is public if you want to see how the training is actually going.',
    href: 'https://running.nickvolgas.com',
    cta: 'running.nickvolgas.com',
    icon: 'run',
  },
  {
    title: 'Climbing',
    blurb:
      'Sport climbing, bouldering, and occasionally some trad.',
    href: 'https://www.mountainproject.com/user/201696612/nicholas-volgas',
    icon: 'mountain',
  },
  {
    title: 'Vinyl',
    blurb:
      'A collection that has outgrown its shelf, catalogued properly because of course I built a site for it.',
    href: 'https://records.nickvolgas.com',
    cta: 'records.nickvolgas.com',
    icon: 'vinyl',
  },
  {
    title: 'Home automation',
    blurb:
      'The house is a permanent side project: sensors, controllers, and the occasional automation my family politely asks me to roll back.',
    icon: 'home',
  },
  {
    title: 'Dad duty',
    blurb:
      'Two little girls, which is the most demanding and by far the best role on this page.',
    icon: 'family',
  },
]
